<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import VuePdfEmbed from "vue-pdf-embed";
import "vue-pdf-embed/dist/styles/annotationLayer.css";
import "vue-pdf-embed/dist/styles/textLayer.css";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
  OverlayWaiting,
  useSnackbarStore,
  SnackbarEnum,
  Banner,
  DialogConfirm,
} from "@es-bill/es-bill-common-components";
import {
  useContractSigning,
  type AgreementDetails,
} from "@/composables/useContractSigning";
import type { ContractSigningError, ErrorType } from "@/types/contractSigning";
import {
  getErrorTypeFromStatus,
  getErrorConfigFromStatus,
  PDF_ERROR_CONFIG,
} from "@/utils/digitalAgreementErrorHandling";
import {
  downloadPdfBlob,
  createBlobUrl,
  revokeBlobUrl,
} from "@/utils/pdfUtils";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const snackbarStore = useSnackbarStore();
const { mobile } = useDisplay();

const { fetchAgreementWithPdf, acceptAgreement, declineAgreement } =
  useContractSigning();

// State
const agreement = ref<AgreementDetails | null>(null);
const pdfBlobUrl = ref<string | null>(null);
const loading = ref(true);
const loadingPdf = ref(false);
const processing = ref(false);
const error = ref<string | null>(null);
const errorType = ref<ErrorType>(null);
const isAccepted = ref(false);
const isDeclined = ref(false);
const hasScrolledToBottom = ref(false);
const pdfViewerContainerRef = ref<HTMLElement | null>(null);
const checkboxAccepted = ref(false);
const declineDialogVisible = ref(false);
const declineReason = ref("");
const declineReasonError = ref("");

const uniqueKey = computed(() => route.params.uniqueKey as string);
const isMobileDevice = computed(() => mobile.value);

// PDF source configuration with CMaps and image resources for cross-browser compatibility
const pdfSource = computed(() => {
  if (!pdfBlobUrl.value) return null;
  return {
    cMapUrl: "https://unpkg.com/pdfjs-dist@latest/cmaps/",
    url: pdfBlobUrl.value,
  };
});

// Check if contract is in rejected/expired/not found state
const isRejectedOrExpiredOrNotFound = computed(() => {
  if (!errorType.value) return isDeclined.value;
  return errorType.value === 'expired' || errorType.value === 'invalid' || errorType.value === 'declined' || errorType.value === 'alreadyDeclined' || isDeclined.value;
});

// Check if we should show rejection message instead of PDF
const shouldShowRejectionMessage = computed(() => isRejectedOrExpiredOrNotFound.value);

// Get the appropriate message for rejected/expired/not found state
const rejectionMessage = computed(() => {
  if (errorType.value === 'alreadyDeclined') {
    return {
      title: t('contractSigning.errorAlreadyDeclined'),
      description: t('contractSigning.errorAlreadyDeclinedDescription')
    };
  }
  if (isDeclined.value || errorType.value === 'declined') {
    return {
      title: t('contractSigning.agreementDeclined'),
      description: t('contractSigning.agreementDeclinedDescription')
    };
  }
  if (errorType.value === 'expired') {
    return {
      title: t('contractSigning.errorExpired'),
      description: t('contractSigning.errorExpiredDescription')
    };
  }
  if (errorType.value === 'invalid') {
    return {
      title: t('contractSigning.errorInvalidHash'),
      description: t('contractSigning.errorInvalidHashDescription')
    };
  }
  return null;
});

// Check if user can interact with the agreement
const canInteract = computed(
  () =>
    !isAccepted.value &&
    !isDeclined.value &&
    !processing.value &&
    !loading.value &&
    !isRejectedOrExpiredOrNotFound.value &&
    agreement.value !== null
);

// Check if validation message should be shown (checkbox checked but PDF not scrolled on desktop)
const showScrollValidationMessage = computed(() => {
  return (
    checkboxAccepted.value &&
    !isMobileDevice.value &&
    !hasScrolledToBottom.value &&
    !isRejectedOrExpiredOrNotFound.value &&
    agreement.value !== null &&
    !loading.value &&
    !isAccepted.value &&
    !isDeclined.value
  );
});

// Check if accept button should be enabled
// Requires: checkbox checked AND (mobile OR scrolled to bottom) AND not in error states
const canAccept = computed(() => {
  if (!checkboxAccepted.value || isRejectedOrExpiredOrNotFound.value) {
    return false;
  }
  // On mobile, just need checkbox checked
  if (isMobileDevice.value) {
    return true;
  }
  // On desktop, need checkbox checked AND scrolled to bottom
  return hasScrolledToBottom.value;
});

// Load agreement details on mount
onMounted(async () => {
  await loadAgreement();
});

// Clean up blob URL on unmount
onUnmounted(() => {
  revokeBlobUrl(pdfBlobUrl.value);
  pdfBlobUrl.value = null;
  // Remove scroll listener
  if (pdfViewerContainerRef.value) {
    pdfViewerContainerRef.value.removeEventListener("scroll", handleScroll);
  }
});

/**
 * Load agreement details and PDF
 */
async function loadAgreement() {
  loading.value = true;
  loadingPdf.value = true;
  error.value = null;
  errorType.value = null;

  try {
    const agreementData = await fetchAgreementWithPdf(uniqueKey.value);
    agreement.value = agreementData;

    // Set PDF blob URL if PDF is available
    if (agreementData.pdfBlob) {
      pdfBlobUrl.value = createBlobUrl(agreementData.pdfBlob);
    }
  } catch (err) {
    console.error("[ContractSigning] Error loading agreement:", err);

    const contractError = err as ContractSigningError;

    // If error has responseData with metadata, set agreement first
    if (
      contractError.responseData &&
      (contractError.status === 403 || contractError.status === 409 || contractError.status === 410 || contractError.status === 404)
    ) {
      // Try to get community name from response headers first, then from responseData
      let communityName: string | undefined = undefined;
      if (contractError.response && 'headers' in contractError.response && contractError.response.headers) {
        const headers = contractError.response.headers;
        communityName =
          (headers['X-Community-Name'] as string) ||
          (headers['x-community-name'] as string) ||
          undefined;
      }
      // Fallback to responseData if not in headers
      if (!communityName && contractError.responseData?.communityName) {
        communityName = contractError.responseData.communityName;
      }

      agreement.value = {
        uniqueKey: contractError.responseData.uniqueKey || uniqueKey.value,
        communityName: communityName,
      };
      if (contractError.status === 409) {
        isAccepted.value = true;
        // Navigate to FinishedContractSigning when already accepted
        router.push({ name: "FinishedContractSigning" });
        return;
      }
      // For 403 (already declined) and other errors, let handleError set the error type
    }

    handleError(contractError);
  } finally {
    loading.value = false;
    loadingPdf.value = false;
  }
}

/**
 * Show error snackbar (only for invalid/expired errors)
 */
function showErrorSnackbar(
  errorConfig:
    | typeof PDF_ERROR_CONFIG
    | ReturnType<typeof getErrorConfigFromStatus>
) {
  // Only show snackbar for invalid (404) or expired (410) errors
  const status = errorConfig.errorType === "invalid" || errorConfig.errorType === "expired";
  if (status) {
    snackbarStore.showSnackbar(
      t(errorConfig.i18nKey),
      t(errorConfig.i18nDescriptionKey),
      SnackbarEnum[errorConfig.snackbarType],
      5000,
      []
    );
  }
}

/**
 * Generate PDF filename based on community name
 */
function getPdfFilename(communityName?: string): string {
  const baseName = "Energieabgabe- und Energiebezugsvertrag";
  if (communityName) {
    return `${baseName} - ${communityName}.pdf`;
  }
  return `${baseName}.pdf`;
}

/**
 * Handle PDF download (for mobile)
 */
async function handleDownloadPdf() {
  if (!uniqueKey.value) return;

  // Use existing PDF blob if available, otherwise fetch again
  if (agreement.value?.pdfBlob) {
    const filename = getPdfFilename(agreement.value.communityName);
    downloadPdfBlob(agreement.value.pdfBlob, filename);
    return;
  }

  // If no PDF blob, try to fetch again
  loadingPdf.value = true;

  try {
    const agreementData = await fetchAgreementWithPdf(uniqueKey.value);
    if (agreementData.pdfBlob) {
      const filename = getPdfFilename(agreementData.communityName);
      downloadPdfBlob(agreementData.pdfBlob, filename);
    }
  } catch {
    // Error handling is done in loadAgreement
  } finally {
    loadingPdf.value = false;
  }
}

/**
 * Handle accept
 */
async function handleAccept() {
  if (!uniqueKey.value || processing.value) return;

  processing.value = true;

  try {
    await acceptAgreement(uniqueKey.value);
    isAccepted.value = true;
    // Navigate to FinishedContractSigning
    router.push({ name: "FinishedContractSigning" });
  } catch (err) {
    handleError(err as ContractSigningError);
  } finally {
    processing.value = false;
  }
}

/**
 * Handle decline
 */
async function handleDecline() {
  if (!uniqueKey.value || processing.value) return;

  const trimmedReason = declineReason.value.trim();
  if (!trimmedReason) {
    declineReasonError.value =
      t("contractSigning.declineReasonRequired");
    return;
  }

  declineReasonError.value = "";

  processing.value = true;

  try {
    await declineAgreement(uniqueKey.value, trimmedReason);
    isDeclined.value = true;
    declineDialogVisible.value = false;
    declineReason.value = "";
    // Keep user on page - no redirect
  } catch (err) {
    handleError(err as ContractSigningError);
  } finally {
    processing.value = false;
  }
}

function openDeclineDialog() {
  if (!canInteract.value) return;
  declineDialogVisible.value = true;
  declineReasonError.value = "";
}

function cancelDeclineDialog() {
  declineDialogVisible.value = false;
  declineReason.value = "";
  declineReasonError.value = "";
}

/**
 * Handle scroll event to check if user has scrolled to bottom
 * Once the user has scrolled to the bottom, buttons remain enabled even if they scroll up
 */
function handleScroll() {
  if (!pdfViewerContainerRef.value || hasScrolledToBottom.value) return;

  const container = pdfViewerContainerRef.value;
  const scrollTop = container.scrollTop;
  const scrollHeight = container.scrollHeight;
  const clientHeight = container.clientHeight;

  // Check if scrolled to bottom (with 50px threshold for better UX)
  const threshold = 50;
  const isAtBottom =
    scrollTop + clientHeight >= scrollHeight - threshold ||
    scrollHeight <= clientHeight; // If content fits without scrolling

  // Only set to true, never back to false
  if (isAtBottom) {
    hasScrolledToBottom.value = true;
  }
}

/**
 * Handle PDF rendered successfully
 */
async function handlePdfRendered() {
  console.log("[ContractSigning] PDF rendered successfully");
  loadingPdf.value = false;
  // Set up scroll listener after PDF is rendered and DOM is updated
  await nextTick();
  if (!isMobileDevice.value && pdfViewerContainerRef.value) {
    pdfViewerContainerRef.value.addEventListener("scroll", handleScroll);
    // Check initial scroll position (in case PDF fits without scrolling)
    handleScroll();
  }
}

/**
 * Handle PDF loading/rendering failure
 */
function handlePdfLoadingFailed(error: unknown) {
  console.error("[ContractSigning] PDF loading/rendering failed:", error);
  errorType.value = "pdf";
  loadingPdf.value = false;
  // Don't show snackbar for PDF errors - error is already shown in UI
}

/**
 * Handle errors
 */
function handleError(err: ContractSigningError) {
  const status = err.status || err.response?.status;
  const responseData = err.response?.data;
  const errorMessage =
    (responseData && typeof responseData === "object" && "error" in responseData
      ? responseData.error
      : null) || err.message;

  console.error("[ContractSigning] Handling error:", {
    status,
    errorMessage,
    err,
    response: err.response,
    request: err.request,
    config: err.config,
  });

  const errorConfig = status
    ? getErrorConfigFromStatus(status)
    : getErrorConfigFromStatus(undefined);
  errorType.value = getErrorTypeFromStatus(status);
  error.value = t(errorConfig.i18nKey);

  // Set isAccepted for already accepted status
  if (status === 409) {
    isAccepted.value = true;
    // Navigate to FinishedContractSigning when already accepted
    router.push({ name: "FinishedContractSigning" });
    return;
  }

  // Set error type for already declined status (403 from backend means already declined)
  // Don't set isDeclined here - that's only for when user declines themselves
  if (status === 403) {
    // Error type is already set above, just return to show the message
    return;
  }

  showErrorSnackbar(errorConfig);
}
</script>

<template>
  <v-container class="containerStandard mainContainer">
    <OverlayWaiting :overlayActive="loading" />

    <div v-if="agreement && !loading && !isAccepted" class="d-flex flex-column agreementContent">
      <div v-if="agreement.communityName" class="d-flex flex-column mb-2 mb-md-3">
        <p class="textCommunityName">{{ agreement.communityName }}</p>
      </div>

      <div v-if="!(shouldShowRejectionMessage && rejectionMessage)" class="d-flex flex-column mb-2 mb-md-3">
        <p class="textHeadline">{{ t("contractSigning.pageTitle") }}</p>
      </div>

      <div v-if="!(shouldShowRejectionMessage && rejectionMessage)" class="d-flex flex-column mb-4 mb-md-6">
        <p class="textDescriptor">
          {{ t("contractSigning.introText") }}
        </p>
      </div>

      <v-card class="pdfContainer elevation-0 evelation-md-1">
        <!-- Show rejection message instead of PDF when contract is rejected/expired/not found -->
        <div v-if="shouldShowRejectionMessage && rejectionMessage" class="d-flex flex-column pa-4 pa-md-8">
          <p class="textHeadline">{{ rejectionMessage.title }}</p>
          <p class="textDescriptor mt-2">{{ rejectionMessage.description }}</p>
        </div>
        <!-- Show PDF viewer when contract is available -->
        <template v-else>
          <div v-if="!isMobileDevice" class="pdfPreview">
            <OverlayWaiting :overlayActive="loadingPdf" />
            <div
              v-if="pdfSource"
              ref="pdfViewerContainerRef"
              class="pdfViewerContainer"
            >
              <VuePdfEmbed
                :source="pdfSource"
                text-layer
                annotation-layer
                image-resources-path="https://unpkg.com/pdfjs-dist@latest/web/images/"
                class="pdfViewer"
                @rendered="handlePdfRendered"
                @loading-failed="handlePdfLoadingFailed"
                @rendering-failed="handlePdfLoadingFailed"
              />
            </div>
            <div class="d-flex flex-column pt-8 w-100">
              <TertiaryButton
                class="downloadContractButton"
                :text="t('contractSigning.downloadPdf')"
                :loading="loadingPdf"
                @click="handleDownloadPdf"
              />
            </div>
          </div>

          <div class="d-flex flex-column pa-3 pa-md-4 mobilePdfAction" v-if="isMobileDevice">
            <TertiaryButton
              class="w-100 downloadContractButton"
              :text="t('contractSigning.viewContract')"
              :loading="loadingPdf"
              @click="handleDownloadPdf"
              prepend-icon="mdi-file-document"
            />
          </div>
        </template>
      </v-card>

      <OverlayWaiting :overlayActive="processing" />
      <div v-if="!shouldShowRejectionMessage" class="d-flex flex-column gap-4 gap-lg-6 mt-4 mt-lg-6 buttonContainer">
        <v-checkbox
          v-model="checkboxAccepted"
          :label="t('contractSigning.acceptCheckbox')"
          :disabled="isRejectedOrExpiredOrNotFound"
          hide-details
          color="primary"
          class="checkboxContainer"
        />
        <Banner
          v-if="showScrollValidationMessage"
          type="Warning"
          :description="t('contractSigning.scrollRequiredMessage')"
          class="validation-message mb-4"
        />
<!--        <Banner-->
<!--          type="Info"-->
<!--          :title="t('contractSigning.contactInfoMessage')"-->
<!--          :description="contactInfoDescription"-->
<!--          class="mb-4 contact-info-banner"-->
<!--        />-->
        <div class="d-flex flex-column flex-lg-row justify-space-between gap-4 gap-lg-6 mt-8">

          <SecondaryButton
            class="w-100 w-lg-auto order-2 order-lg-1"
            :text="t('contractSigning.decline')"
            :disabled="!canInteract || isRejectedOrExpiredOrNotFound"
            :loading="processing"
            @click="openDeclineDialog"
          />
          <PrimaryButton
            class="w-100 w-lg-auto order-1 order-lg-2 ml-lg-4 mb-4 mb-lg-0"
            :text="t('contractSigning.acceptContract')"
            :disabled="!canAccept"
            :loading="processing"
            @click="handleAccept"
          />
        </div>
      </div>

      <DialogConfirm
        v-model="declineDialogVisible"
        :title="t('contractSigning.confirmDeclineTitle')"
        :message="t('contractSigning.confirmDeclineMessage')"
        :confirm-text="t('contractSigning.decline')"
        :cancel-text="t('cancel')"
        :confirm-loading="processing"
        :confirm-disabled="processing"
        :close-on-confirm="false"
        @confirm="handleDecline"
        @cancel="cancelDeclineDialog"
      >
        <template #content>
          <v-textarea
            v-model="declineReason"
            :label="t('contractSigning.declineReasonLabel')"
            :hint="t('contractSigning.declineReasonHint')"
            :error-messages="declineReasonError ? [declineReasonError] : []"
            auto-grow
            rows="3"
            class="mt-4"
            density="comfortable"
          />
        </template>
      </DialogConfirm>

    </div>
  </v-container>
</template>

<style scoped lang="scss">
.mainContainer {
  display: flex;
  padding: 16px 12px 40px 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  max-width: 100%;

  @media (min-width: 600px) {
    padding: 24px 16px 60px 16px;
    gap: 24px;
  }
}

.agreementContent {
  width: 100%;
}

.pdfContainer {
  width: 100%;
  max-width: 900px;
  margin: auto;
}

.pdfPreview {
  width: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;

  @media (min-width: 600px) {
    min-height: 600px;
  }
}

.pdfViewerContainer {
  width: 100%;
  max-height: 60vh;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background-color: rgb(var(--v-theme-surface-light));

  @media (min-width: 600px) {
    max-height: 70vh;
    min-height: 600px;
  }
}

.pdfViewer {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  :deep(.vue-pdf-embed) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  :deep(canvas) {
    margin: 0 auto 0 0;
  }
}

.mobilePdfAction {
  width: 100%;
}


.buttonContainer {
  width: 100%;
  max-width: 900px;
  margin: auto;
}

.textCommunityName {
  font-size: var(--font-size-h4);
  line-height: var(--line-height-h4);
  font-weight: 500;
  text-align: left;
  margin: 0;
  color: rgb(var(--v-theme-on-surface));
  opacity: var(--v-high-emphasis-opacity);

  @media (max-width: 599px) {
    font-size: calc(var(--font-size-h4) * 0.9);
  }
}

.validation-message {  width: 100%;}

:deep(.contact-info-banner .description) {
  white-space: pre-line;
}
</style>
